import { caseTransformedText } from "../core/types.js";

/**
 * Cached text measurement at font-stretch 100%. Ordinary runs use canvas;
 * variants and OpenType features canvas cannot express use batched DOM
 * probes. See calibrate.ts for expansion widths.
 */

export interface FontSpec {
  style: string;
  weight: string;
  sizePx: number;
  family: string;
  letterSpacingPx: number;
  wordSpacingPx: number;
  /** The author's own font-stretch, e.g. "100%". */
  stretch: string;
  /** Computed font-variation-settings ("normal" when unset). */
  variationSettings: string;
  /** Every computed font-variant longhand. These participate in the cache
   * key because OpenType substitutions can change glyph advances. */
  variantAlternates: string;
  variantCaps: string;
  variantEastAsian: string;
  variantEmoji: string;
  /** Computed `hyphens` ("manual" default); "none" suppresses hyphenation
   * in this run. Not part of the cache key — it doesn't affect widths. */
  hyphens: string;
  /** Computed font-variant-ligatures. */
  ligatures: string;
  /** Computed font-feature-settings. */
  featureSettings: string;
  /** Computed font-variant-numeric. */
  numeric: string;
  variantPosition: string;
  /**
   * Computed `text-transform` ("none" default). Part of the cache key, and
   * enough on its own to force DOM measurement: canvas has no equivalent, but
   * a probe carrying the property renders exactly what the run will.
   *
   * Nothing here ever maps the text itself. The probe is fed SOURCE text and
   * returns the width of its rendered form, so a mapping that changes length
   * (`ß`→`SS`, `ﬁ`→`FI`) never reaches the item model, whose offsets stay
   * source offsets — and locale-sensitive casing (Turkish dotless i, Greek
   * final sigma) comes out right without reading the run's `lang`.
   */
  textTransform: string;
  /**
   * Computed direction, applied as canvas context state so RTL words are
   * shaped under the same base direction the DOM renders them with.
   * Deliberately NOT part of the cache key: measureText advances are
   * direction-independent in every engine — words are measured whole, so
   * Arabic joining and bidi-neutral reordering are internal to the string
   * and cannot change its total advance (verified empirically across
   * chromium/firefox/webkit for pointed Hebrew, joined Arabic, digits and
   * mirrored brackets; enforced by an e2e guard test).
   */
  direction: "ltr" | "rtl";
  /**
   * Canonical cache key. Two specs sharing it must be interchangeable for
   * EVERY measurement: the reader can hand out either one (a paragraph holds
   * separate entries for runs differing only in `hyphens`, and the per-key
   * measure map keeps one of them). So a field left out of this key may not be
   * read by anything that measures — `direction` qualifies only because
   * advances are direction-independent AND a paragraph's direction is uniform
   * (mixed-direction content bails in read.ts).
   */
  key: string;
  /**
   * Precomputed `requiresDomMeasurement` verdict. It is a pure function of
   * the variant/feature fields, and it was being recomputed — eight string
   * comparisons — on every single width lookup (tens of thousands per cold
   * enhance). Read it through `requiresDomMeasurement` rather than directly.
   */
  needsDomMeasurement: boolean;
}

export function fontSpecOf(style: CSSStyleDeclaration): FontSpec {
  const letterSpacing = style.letterSpacing === "normal" ? 0 : parseFloat(style.letterSpacing) || 0;
  const wordSpacing = parseFloat(style.wordSpacing) || 0;
  const computed = (property: string, fallback = "normal"): string =>
    style.getPropertyValue(property).trim() || fallback;
  const spec: FontSpec = {
    style: style.fontStyle,
    weight: style.fontWeight,
    sizePx: parseFloat(style.fontSize) || 16,
    family: style.fontFamily,
    letterSpacingPx: letterSpacing,
    wordSpacingPx: wordSpacing,
    stretch: style.fontStretch || "100%",
    variationSettings: style.fontVariationSettings || "normal",
    variantAlternates: computed("font-variant-alternates"),
    variantCaps: computed("font-variant-caps"),
    variantEastAsian: computed("font-variant-east-asian"),
    // font-variant-emoji is newer than the other longhands and is absent
    // from older CSSStyleDeclaration typings/engines. An unsupported
    // property computes to the same effective initial value.
    variantEmoji: computed("font-variant-emoji"),
    hyphens:
      style.hyphens ||
      (style as CSSStyleDeclaration & { webkitHyphens?: string }).webkitHyphens ||
      "manual",
    ligatures: computed("font-variant-ligatures"),
    featureSettings: computed("font-feature-settings"),
    numeric: computed("font-variant-numeric"),
    variantPosition: computed("font-variant-position"),
    textTransform: computed("text-transform", "none"),
    direction: style.direction === "rtl" ? "rtl" : "ltr",
    key: "",
    needsDomMeasurement: false,
  };
  spec.key = [
    spec.style,
    spec.weight,
    spec.sizePx,
    spec.family,
    spec.letterSpacingPx,
    spec.wordSpacingPx,
    spec.stretch,
    spec.variationSettings,
    spec.variantAlternates,
    spec.variantCaps,
    spec.variantEastAsian,
    spec.variantEmoji,
    spec.ligatures,
    spec.featureSettings,
    spec.numeric,
    spec.variantPosition,
    spec.textTransform,
  ].join("|");
  spec.needsDomMeasurement =
    spec.textTransform !== "none" ||
    spec.variantCaps !== "normal" ||
    spec.variantAlternates !== "normal" ||
    spec.variantEastAsian !== "normal" ||
    spec.variantEmoji !== "normal" ||
    spec.ligatures !== "normal" ||
    spec.featureSettings !== "normal" ||
    spec.numeric !== "normal" ||
    spec.variantPosition !== "normal";
  return spec;
}

/** The canvas shorthand accepts style/weight/size/family only. */
export function ctxFontOf(spec: FontSpec): string {
  const style = spec.style === "normal" ? "" : spec.style + " ";
  const weight = spec.weight === "400" || spec.weight === "normal" ? "" : spec.weight + " ";
  return `${style}${weight}${spec.sizePx}px ${spec.family}`;
}

type MeasureCtx = CanvasRenderingContext2D;

let sharedCtx: MeasureCtx | null = null;
let currentKey = "";
/** Tracked separately from the key (direction is not in it — see FontSpec). */
let currentDirection: "ltr" | "rtl" = "ltr";

function getCtx(): MeasureCtx {
  if (sharedCtx === null) {
    // Prefer a regular canvas: Firefox's OffscreenCanvas 2D context desyncs
    // its font state after fontVariantCaps changes — resetting it back to
    // "normal" (and reassigning .font) leaves SHAPING in small-caps while
    // the getters read normal, silently inflating every subsequent
    // measurement by the small-caps delta. Element-backed contexts behave
    // correctly in every engine; OffscreenCanvas is only the no-DOM fallback.
    const canvas =
      typeof document !== "undefined"
        ? document.createElement("canvas")
        : new OffscreenCanvas(0, 0);
    sharedCtx = (canvas as HTMLCanvasElement).getContext("2d") as unknown as MeasureCtx;
    if (sharedCtx === null) throw new Error("justif: no 2d canvas context");
  }
  return sharedCtx;
}

/** Dedicated context for raw font-string probes (font readiness / change
 * detection). Separate from the shared measuring context on purpose:
 * probes set `ctx.font` from a bare shorthand, which would silently
 * desync setFont's currentKey memoization and skip its caps/spacing
 * resets. Element-backed for the same Firefox reason as getCtx. */
let probeCtx: MeasureCtx | null = null;

/**
 * Canvas advance of `text` under a raw ctx-font shorthand, uncached.
 * Measured in chunks so a pathologically large sample (thousands of
 * distinct ideographs) never becomes one giant shaping call; the chunk
 * edge steps over a high surrogate rather than splitting a pair.
 */
export function probeAdvance(font: string, text: string): number {
  if (probeCtx === null) {
    const canvas =
      typeof document !== "undefined"
        ? document.createElement("canvas")
        : new OffscreenCanvas(0, 0);
    probeCtx = (canvas as HTMLCanvasElement).getContext("2d") as unknown as MeasureCtx;
    if (probeCtx === null) throw new Error("justif: no 2d canvas context");
  }
  probeCtx.font = font;
  let width = 0;
  for (let at = 0; at < text.length; ) {
    let end = Math.min(text.length, at + 2048);
    const code = text.charCodeAt(end - 1);
    if (code >= 0xd800 && code <= 0xdbff && end < text.length) end++;
    width += probeCtx.measureText(text.slice(at, end)).width;
    at = end;
  }
  return width;
}

function setFont(ctx: MeasureCtx, spec: FontSpec): void {
  if (currentKey === spec.key && currentDirection === spec.direction) return;
  // Base direction for shaping; the default "inherit" would follow the
  // (detached) canvas element rather than the run being measured.
  if ("direction" in ctx) ctx.direction = spec.direction;
  currentDirection = spec.direction;
  ctx.font = ctxFontOf(spec);
  if ("letterSpacing" in ctx) ctx.letterSpacing = spec.letterSpacingPx + "px";
  if ("wordSpacing" in ctx) ctx.wordSpacing = spec.wordSpacingPx + "px";
  // Always reset caps state. Engines differ on whether assigning the font
  // shorthand also resets this longhand, so relying on either retained or
  // reset state would make the next measurement engine-dependent. Canvas
  // never MEASURES caps variants — those runs take the DOM path (see
  // requiresDomMeasurement) — so the explicit value is always `normal`.
  if ("fontVariantCaps" in ctx) {
    (ctx as { fontVariantCaps: string }).fontVariantCaps = "normal";
  }
  currentKey = spec.key;
}

/** Apply every width-affecting font property represented by FontSpec to a
 * DOM probe. The low-level feature setting is deliberately written after
 * the font-variant longhands, matching the cascade already computed on the
 * source run and OpenType's feature resolution order. */
export function applyFontSpec(el: HTMLElement, spec: FontSpec): void {
  el.style.fontStyle = spec.style;
  el.style.fontWeight = spec.weight;
  el.style.fontSize = spec.sizePx + "px";
  el.style.fontFamily = spec.family;
  el.style.letterSpacing = spec.letterSpacingPx + "px";
  el.style.wordSpacing = spec.wordSpacingPx + "px";
  el.style.direction = spec.direction;
  el.style.fontStretch = spec.stretch;
  el.style.fontVariationSettings = spec.variationSettings;
  // Not a font property: it decides which glyphs get shaped at all, so the
  // probe renders the run's actual text rather than its source.
  el.style.setProperty("text-transform", spec.textTransform);
  el.style.setProperty("font-variant-alternates", spec.variantAlternates);
  el.style.setProperty("font-variant-caps", spec.variantCaps);
  el.style.setProperty("font-variant-east-asian", spec.variantEastAsian);
  el.style.setProperty("font-variant-emoji", spec.variantEmoji);
  el.style.setProperty("font-variant-ligatures", spec.ligatures);
  el.style.setProperty("font-variant-numeric", spec.numeric);
  el.style.setProperty("font-variant-position", spec.variantPosition);
  el.style.setProperty("font-feature-settings", spec.featureSettings);
}

/** True when the run needs the DOM shaper rather than canvas measureText.
 * A `text-transform` always takes the DOM probe: canvas measures the string it
 * is handed, and the run renders a different one. Caps variants always do too,
 * because canvas fontVariantCaps support is port-dependent — absent in Mac
 * WebKit, present-but-divergent from DOM
 * shaping in GTK WebKit (Playwright's Linux build measured all-small-caps
 * ~11px/line apart), and Firefox's OffscreenCanvas has desynced its caps
 * state before. A styled DOM probe shapes with exactly the engine that
 * renders. Every other variant/feature value likewise. */
export function requiresDomMeasurement(spec: FontSpec): boolean {
  return spec.needsDomMeasurement;
}

/** Paragraphs whose styling neither measurement path can reproduce bail. */
export function supportsSpec(spec: FontSpec): boolean {
  // Canvas measures at default stretch with no variation settings: an
  // author-condensed/expanded or variation-pinned run is still outside the
  // expansion model: our per-line font-stretch would overwrite it.
  if (spec.stretch !== "100%" && spec.stretch !== "normal") return false;
  if (spec.variationSettings !== "normal") return false;
  return true;
}

const widthCache = new Map<string, Map<string, number>>();
const domWidthCache = new Map<string, Map<string, number>>();
/**
 * Ceiling on cached measurements, across every font. Word widths repay their
 * entry many times over, but hyphenation measures each fragment over token
 * PREFIXES ("beau", "beauti", …) and those strings are typically used once —
 * a 15k-word document caches ~26k entries, and nothing evicts them while the
 * fonts stay settled. Trimming keeps a long-lived single-page app from
 * growing without bound; these are pure caches, so a dropped entry only costs
 * one re-measurement and can never change a width.
 */
const MAX_CACHED_WIDTHS = 150_000;
let cachedWidths = 0;

/** Drop the oldest half of every per-font map (Map preserves insertion
 * order, so this is an approximate FIFO — cheap, and it keeps the entries
 * most likely to recur). */
function trimWidthCaches(): void {
  for (const cache of [widthCache, domWidthCache]) {
    for (const [key, perFont] of cache) {
      // A map of one entry halves to zero: without this the counter stays
      // pinned over the cap and every later measurement re-walks every font.
      const drop = perFont.size >> 1;
      if (drop === 0) {
        if (perFont.size === 0) cache.delete(key);
        continue;
      }
      let dropped = 0;
      for (const text of perFont.keys()) {
        if (dropped >= drop) break;
        perFont.delete(text);
        dropped++;
      }
      cachedWidths -= dropped;
    }
  }
}

function noteCachedWidth(): void {
  if (++cachedWidths > MAX_CACHED_WIDTHS) trimWidthCaches();
}
const pendingDomWidths = new Map<string, { spec: FontSpec; texts: Set<string> }>();
let collectingDomWidths = false;
let segmenter: Intl.Segmenter | null | undefined;

function graphemeCount(text: string): number {
  if (segmenter === undefined) {
    segmenter = typeof Intl !== "undefined" && "Segmenter" in Intl ? new Intl.Segmenter() : null;
  }
  if (segmenter === null) return Array.from(text).length;
  let n = 0;
  for (const _ of segmenter.segment(text)) n++;
  return n;
}

/** Canvas advance under `spec`. Used directly for ordinary runs and as a
 * throwaway estimate while discovering all strings a DOM-measured
 * paragraph will need. */
function measureCanvasWidth(text: string, spec: FontSpec): number {
  let perFont = widthCache.get(spec.key);
  if (perFont === undefined) {
    perFont = new Map();
    widthCache.set(spec.key, perFont);
  }
  const hit = perFont.get(text);
  if (hit !== undefined) return hit;

  const ctx = getCtx();
  setFont(ctx, spec);
  let width = ctx.measureText(text).width;
  if (!("letterSpacing" in ctx)) {
    // Arithmetic fallback (pre-2025 Safari): CSS adds tracking after every
    // grapheme and word-spacing to every space.
    if (spec.letterSpacingPx !== 0) width += spec.letterSpacingPx * graphemeCount(text);
    if (spec.wordSpacingPx !== 0) {
      let spaces = 0;
      for (const ch of text) if (ch === " " || ch === "\u00A0") spaces++;
      width += spec.wordSpacingPx * spaces;
    }
  }
  perFont.set(text, width);
  noteCachedWidth();
  return width;
}

function cachedDomWidth(text: string, spec: FontSpec): number | undefined {
  return domWidthCache.get(spec.key)?.get(text);
}

function queueDomWidth(text: string, spec: FontSpec): void {
  let pending = pendingDomWidths.get(spec.key);
  if (pending === undefined) {
    pending = { spec, texts: new Set() };
    pendingDomWidths.set(spec.key, pending);
  }
  pending.texts.add(text);
}

/** Measure all queued variant-bearing strings in one DOM layout. */
function flushDomWidths(): void {
  if (pendingDomWidths.size === 0) return;
  if (typeof document === "undefined" || document.body === null) {
    pendingDomWidths.clear();
    return;
  }

  const host = document.createElement("div");
  host.style.cssText =
    "position:absolute;left:-100000px;top:0;visibility:hidden;pointer-events:none;" +
    "white-space:pre;width:max-content;contain:layout style paint;";
  const probes: Array<{ span: HTMLSpanElement; text: string; spec: FontSpec }> = [];

  for (const { spec, texts } of pendingDomWidths.values()) {
    let perFont = domWidthCache.get(spec.key);
    if (perFont === undefined) {
      perFont = new Map();
      domWidthCache.set(spec.key, perFont);
    }
    for (const text of texts) {
      if (perFont.has(text)) continue;
      if (text.length === 0) {
        perFont.set(text, 0);
        noteCachedWidth();
        continue;
      }
      const span = document.createElement("span");
      applyFontSpec(span, spec);
      span.style.display = "block";
      span.style.width = "max-content";
      span.style.whiteSpace = "pre";
      span.textContent = text;
      host.append(span);
      probes.push({ span, text, spec });
    }
  }
  pendingDomWidths.clear();
  document.body.append(host);
  try {
    // Appending every probe before the first read lets the engine shape and
    // lay them out as one batch rather than forcing a layout per word.
    for (const { span, text, spec } of probes) {
      domWidthCache.get(spec.key)!.set(text, span.getBoundingClientRect().width);
      noteCachedWidth();
    }
  } finally {
    host.remove();
  }
}

/**
 * Run a discovery pass, then resolve all DOM-only measurements together.
 * The callback's results are intentionally disposable: uncached variant
 * widths use canvas estimates during discovery and become exact only after
 * this function returns.
 */
export function collectDomMeasurements<T>(work: () => T): T {
  if (collectingDomWidths) return work();
  collectingDomWidths = true;
  try {
    return work();
  } finally {
    collectingDomWidths = false;
    flushDomWidths();
  }
}

/** Advance of `text` under `spec` at font-stretch 100%, CSS px. */
export function measureWidth(text: string, spec: FontSpec): number {
  if (!requiresDomMeasurement(spec)) return measureCanvasWidth(text, spec);

  const hit = cachedDomWidth(text, spec);
  if (hit !== undefined) return hit;
  queueDomWidth(text, spec);
  if (collectingDomWidths) return measureCanvasWidth(text, spec);

  // A correctly integrated paragraph will have discovered this string in a
  // batch. Keep the primitive safe for direct callers and unusual late
  // paths by resolving a cache miss immediately.
  flushDomWidths();
  return cachedDomWidth(text, spec) ?? measureCanvasWidth(text, spec);
}

const bearingCache = new Map<string, Map<string, { l: number; r: number }>>();

/**
 * Source text → what `spec` renders for it. Only the context-free case
 * mappings are ever set on a spec that reaches here (see
 * `supportedTextTransform` in read.ts), so this is the complete mapping.
 *
 * Callers never need this for WIDTH — the probe carries the property and
 * measures source text directly. It exists for the two questions the rendered
 * string does answer: which glyph a protrusion code should be looked up under,
 * and whether the mapping changes LENGTH, which decides whether source offsets
 * still index the glyph run.
 */
export function transformedText(text: string, spec: FontSpec): string {
  return caseTransformedText(text, spec.textTransform);
}

/**
 * Side bearings of `ch`: distance from each edge of the advance box to the
 * glyph's ink (clamped ≥ 0 when ink spills past the box, as with italics).
 * Used to stop protrusion at the ink edge for monospace runs.
 */
export function measureInkBearings(ch: string, spec: FontSpec): { l: number; r: number } {
  let perFont = bearingCache.get(spec.key);
  if (perFont === undefined) {
    perFont = new Map();
    bearingCache.set(spec.key, perFont);
  }
  const hit = perFont.get(ch);
  if (hit !== undefined) return hit;

  const ctx = getCtx();
  setFont(ctx, spec);
  // Bearings belong to the glyph that RENDERS, and canvas has no
  // text-transform to apply — so map the character before measuring it.
  // Cached under the source character, which is safe because the transform
  // is part of `spec.key` and so of this cache's per-font bucket.
  const m = ctx.measureText(transformedText(ch, spec));
  // actualBoundingBoxLeft is positive when ink extends LEFT of the origin,
  // so the left bearing (origin → ink) is its negation.
  const bearings = {
    l: Math.max(0, -m.actualBoundingBoxLeft),
    r: Math.max(0, m.width - m.actualBoundingBoxRight),
  };
  perFont.set(ch, bearings);
  return bearings;
}

/** True when the font is fixed-pitch (every glyph shares one advance). */
export function isMonospace(spec: FontSpec): boolean {
  return Math.abs(measureWidth("i", spec) - measureWidth("M", spec)) < 0.01;
}

/** Drop cached measurements (call when webfonts finish loading). */
export function clearMeasureCache(): void {
  widthCache.clear();
  domWidthCache.clear();
  cachedWidths = 0;
  pendingDomWidths.clear();
  bearingCache.clear();
  currentKey = "";
}
